import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { daysOfWeek, howDidYouHearAboutUs, months, requestSource } from 'utils/constants'
import { appointmentRequestSchema, type AppointmentRequestFormData } from 'app/utils/schemas'
import { usePhoneFormat } from 'app/utils/hooks/usePhoneFormat'
import { Label } from 'app/components/ui/label'
import { Input } from 'app/components/ui/input'
import { Textarea } from 'app/components/ui/textarea'
import { SmallCheckbox } from 'app/components/ui/checkbox'
import { Button } from 'app/components/ui/button'
import { RadioGroup, RadioGroupItem } from 'app/components/ui/radio-group'
import { Dialog, DialogTrigger, DialogHeader, DialogTitle, DialogClose, DialogContent } from 'app/components/ui/dialog'
import { useAppointmentListSlice } from '../slice'
import { toast } from 'app/components/ui/use-toast'
import { format } from 'date-fns'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem2,
} from "app/components/ui/dropdown-menu"
import { Calendar as CalendarComponent } from "app/components/ui/calendar"
import { MonthYearDropdown } from 'app/components/common/Filters/MonthYearDropdown'
import { formatUSDate } from 'utils/date'

interface Props {
  onSave: () => void;
}

export const AddNewAppointmentRequest = ({ onSave }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  console.log(selectedDate);

  const [requestSourceDropdownOpen, setRequestSourceDropdownOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const { useAddAppointmentMutation } = useAppointmentListSlice();
  const [addAppointment, { isLoading, isError, error }] = useAddAppointmentMutation();
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<AppointmentRequestFormData>({
    resolver: zodResolver(appointmentRequestSchema),
    defaultValues: {
      patientType: 'new',
      preferredDays: [],
      preferredTimes: [],
      source: [],
    },
  });

  const { handlePhoneChange } = usePhoneFormat(setValue);

  const selectedPatientType = watch('patientType');
  const preferredDays = watch('preferredDays');
  const preferredTimes = watch('preferredTimes');
  const source = watch('source');
  const requestSourceValue = watch('requestSource');

  const handleDayChange = (dayId: string, checked: boolean) => {
    const currentDays = preferredDays || [];
    if (checked) {
      setValue('preferredDays', [...currentDays, dayId]);
    } else {
      setValue('preferredDays', currentDays.filter(day => day !== dayId));
    }
  };

  const handleTimeChange = (timeId: string, checked: boolean) => {
    const currentTimes = preferredTimes || [];
    if (checked) {
      setValue('preferredTimes', [...currentTimes, timeId]);
    } else {
      setValue('preferredTimes', currentTimes.filter(time => time !== timeId));
    }
  };

  const handleSourceChange = (sourceId: string, checked: boolean) => {
    if (checked) {
      // Single select: only keep the newly selected item
      setValue('source', [sourceId]);
    } else {
      // If unchecking, clear the selection
      setValue('source', []);
    }
  };

  const handleRequestSourceChange = (sourceKey: string) => {
    setValue('requestSource', sourceKey);
    setRequestSourceDropdownOpen(false);
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setIsCalendarOpen(false);
  };

  const handleDateChange = (day: Date) => {
    setSelectedDate(day);
    setIsCalendarOpen(false);
  };


  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i);

  const calendarMonth = new Date(selectedYear, selectedMonth, 1);

  const handleFormSubmit = (data: AppointmentRequestFormData) => {
    // Format date in local timezone with full ISO format including timezone offset
    const formatDateToLocalISO = (date: Date): string => {
      // Set time to current time to include hours, minutes, seconds, and milliseconds
      const now = new Date();
      const combinedDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        now.getHours(),
        now.getMinutes(),
        now.getSeconds(),
        now.getMilliseconds()
      );
      return combinedDate.toISOString();
    };

    const payload = {
      patientType: data.patientType,
      firstName: data.firstName,
      lastName: data.lastName,
      preferredDays: data.preferredDays,
      preferredTimes: data.preferredTimes,
      servicesInterested: data.servicesInterested,
      message: data.message,
      source: data.source,
      ...(data.phone && { phoneNumber: data.phone }),
      ...(data.email && { email: data.email }),
      ...(selectedDate && { date: formatDateToLocalISO(selectedDate) }),
      ...(data.requestSource && { requestSource: data.requestSource }),
    };
 

    addAppointment(payload).unwrap().then((res) => {
      toast({
        title: 'Appointment request created successfully',
        description: 'Appointment request created successfully',
      });
      reset();
      setSelectedDate(undefined);
      setIsOpen(false);
      onSave();
    }).catch((err) => {
      toast({
        title: 'Failed to create appointment request',
        description: err?.messages || 'Failed to create appointment request',
        variant: 'destructive',
      });
    });
  };

  const handleClose = () => {
    reset();
    setSelectedDate(undefined);
    setIsOpen(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="primary" className="p-2 pr-4 h-10">
          <img src="/images/plus-small.svg" alt="Plus" className="flex-shrink-0 mr-1 size-5" />
          Add New Appointment Request
        </Button>
      </DialogTrigger>
      <DialogContent
        className="gap-0 bg-white max-w-[38.75rem] rounded-lg shadow-[0px_1px_4px_0px_rgba(0,0,0,0.25)] overflow-hidden"
        onInteractOutside={e => e.preventDefault()}
      >
        <DialogHeader className="flex flex-row gap-5 justify-between bg-[#F2F4F8] p-4 rounded-t-lg">
          <DialogTitle className="font-serif text-[#5B73F1] text-base font-semibold leading-[1.2rem]">
            Add New Appointment Request
          </DialogTitle>
          <DialogClose asChild>
            <img
              src="/images/close.svg"
              alt="Close"
              className="size-[0.78663rem] cursor-pointer opacity-80 hover:opacity-100 transition-opacity duration-200"
              onClick={handleClose}
            />
          </DialogClose>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="p-4 pt-3 space-y-6 custom-scrollbar overflow-y-auto h-[calc(100vh-10rem)] max-h-[49.475rem]">
         <div className="flex justify-between items-center">
           <div className="flex gap-3 items-center">
             <p className="text-[#4D5358] text-xs font-light leading-[1.05rem]">
               Request Date
             </p>
             <DropdownMenu open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
               <DropdownMenuTrigger asChild>
                 <button
                   type="button"
                   className="flex gap-3 items-center focus:outline-none"
                   onClick={(e) => {
                     e.preventDefault();
                     e.stopPropagation();
                   }}
                  >
                   {selectedDate ? (
                      <span className="inline-block text-[#343A3F] text-sm font-normal leading-[1.0625rem]">
                        {formatUSDate(selectedDate)}
                     </span>
                   ) : (
                     <img src="/images/calendar-icon.svg" alt="Calendar" className="size-4" />
                    )}
                   <img src="/images/chevron-down.svg" alt="Chevron" />
                 </button>
               </DropdownMenuTrigger>
               <DropdownMenuContent
                 className="p-0 bg-white border border-[#DDE1E6] rounded-lg shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
                 sideOffset={4}
                 align="start"
               >
                 <div className="relative">
                   <div className="flex absolute top-4 right-4 z-10 gap-4 justify-end items-center">
                     <MonthYearDropdown
                       selectedValue={months[selectedMonth]}
                       setSelectedValue={(value) => {
                         setSelectedMonth(months.indexOf(value as string));
                         return true;
                       }}
                       options={months}
                       type="month"
                     />
                     <MonthYearDropdown
                       selectedValue={selectedYear}
                       setSelectedValue={(value) => setSelectedYear(value as number)}
                       options={years}
                       type="year"
                     />
                   </div>
                   <CalendarComponent
                     mode="single"
                     month={calendarMonth}
                     selected={selectedDate}
                     onDayClick={(day) => handleDateChange(day)}
                     className="bg-white"
                     classNames={{
                       nav: "hidden",
                       caption_label: "hidden",
                     }}
                     disabled={(date) => date > new Date()}
                   />
                 </div>
               </DropdownMenuContent>
             </DropdownMenu>
           </div>
           <div className="flex gap-3 items-center">
            <p className="text-[#4D5358] text-xs font-light leading-[1.05rem]">
             Request Source
            </p>
            <DropdownMenu open={requestSourceDropdownOpen} onOpenChange={setRequestSourceDropdownOpen}>
              <DropdownMenuTrigger asChild>
                <div className={`flex items-center justify-between bg-white hover:bg-[#FAFAFA] w-40 h-[1.875rem] px-6 py-3 border rounded-md text-[#343A3F] text-sm font-normal leading-[1.05rem] cursor-pointer ${
                  errors.requestSource ? 'border-[#B3171A] focus:ring-0' : 'border-[#DDE1E6] focus:ring-1 focus:ring-blue-200'
                 }`}
                >
                  <span className="truncate text-[#343A3F] text-xs font-normal leading-[1.05rem]">
                    {requestSourceValue ? requestSource[requestSourceValue as unknown as keyof typeof requestSource] : 'Select request'}
                  </span>
                  <img
                    src="/images/chevron-down.svg"
                    className={`transition-transform duration-300 ease-in-out ${requestSourceDropdownOpen ? 'rotate-180' : ''}`}
                    alt="Chevron"
                  />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="max-h-60 min-w-[9.8125rem] p-0">
                {Object.entries(requestSource).map(([key, label]) => (
                  <DropdownMenuCheckboxItem2
                    key={key}
                    checked={requestSourceValue === key}
                    onCheckedChange={() => handleRequestSourceChange(key)}
                    className={`px-6 py-2 rounded text-sm font-normal leading-[1.05rem] tracking-[-0.00875rem] cursor-pointer ${
                      requestSourceValue === key ? 'bg-[#5B73F1] focus:bg-[#5B73F1] text-white focus:text-[#FAFAFA]' : 'text-[#343A3F]'
                    }`}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {label}
                  </DropdownMenuCheckboxItem2>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {errors.requestSource && (
              <p className="text-[#B3171A] text-xs font-normal leading-normal mt-0.5">{errors.requestSource.message}</p>
            )}
            </div>
            </div>
            <div>
            <RadioGroup
              value={selectedPatientType}
              onValueChange={(value) => setValue('patientType', value as 'new' | 'existing')}
              className="flex flex-row gap-12 items-center"
            >
              <div className="flex gap-1 items-center">
                <RadioGroupItem value="new" id="new" className="size-3 border-[#A2A9B0]" />
                <Label htmlFor="new" className="text-[#343A3F] text-sm font-normal leading-normal mb-0 cursor-pointer">
                  New Patient
                </Label>
              </div>
              <div className="flex gap-1 items-center">
                <RadioGroupItem value="existing" id="existing" className="size-3 border-[#A2A9B0]" />
                <Label htmlFor="existing" className="text-[#343A3F] text-sm font-normal leading-normal mb-0 cursor-pointer">
                  Existing Patient
                </Label>
              </div>
            </RadioGroup>
            {errors.patientType && (
              <p className="mt-1 text-xs text-red-500">{errors.patientType.message}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div className="">
              <Label
                htmlFor="firstName"
                className="w-fit text-[#4D5358] text-xs font-light leading-[1.05rem] mb-1"
              >
                First Name
              </Label>
              <Input
                id="firstName"
                type="text"
                className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.firstName ? 'border-red-500' : ''}`}
                placeholder="|"
                {...register('firstName')}
              />
              {errors.firstName && (
                <p className="mt-1 text-xs text-red-500">{errors.firstName.message}</p>
              )}
            </div>
            <div className="">
              <Label
                htmlFor="lastName"
                className="w-fit text-[#4D5358] text-xs font-light leading-[1.05rem] mb-1"
              >
                Last Name
              </Label>
              <Input
                id="lastName"
                type="text"
                className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.lastName ? 'border-red-500' : ''}`}
                placeholder="|"
                {...register('lastName')}
              />
              {errors.lastName && (
                <p className="mt-1 text-xs text-red-500">{errors.lastName.message}</p>
              )}
            </div>
            <div className="">
              <Label
                htmlFor="phone"
                className="w-fit text-[#4D5358] text-xs font-light leading-[1.05rem] mb-1"
              >
                Phone
              </Label>
              <Input
                id="phone"
                type="tel"
                inputMode="numeric"
                className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.phone ? 'border-red-500' : ''}`}
                placeholder="|"
                {...register('phone')}
                onChange={handlePhoneChange}
              />
              {errors.phone && (
                <p className="mt-1 text-xs text-red-500">{errors.phone.message}</p>
              )}
            </div>
            <div className="">
              <Label
                htmlFor="email"
                className="w-fit text-[#4D5358] text-xs font-light leading-[1.05rem] mb-1"
              >
                Email
              </Label>
              <Input
                id="email"
                type="email"
                className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.email ? 'border-red-500' : ''}`}
                placeholder="|"
                {...register('email')}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-500">{errors.email.message}</p>
              )}
            </div>
          </div>
          <div className="">
            <h6 className="text-[#4D5358] text-xs font-light leading-[1.05rem] mb-2">
              Preferred Appointment Days (Select all the days that work for you)
            </h6>
            <div className="flex gap-3 items-center">
              {daysOfWeek.map((item) => (
                <div key={item.id} className="flex gap-1 items-center">
                  <SmallCheckbox 
                    id={item.id}
                    className="size-3 border-[0.6px] border-[#343A3F] rounded-[0.0625rem] shadow-none"
                    checked={preferredDays?.includes(item.id) || false}
                    onCheckedChange={(checked) => handleDayChange(item.id, checked as boolean)}
                  />
                  <Label htmlFor={item.id} className="text-[#343A3F] text-sm font-normal leading-[1.05rem] tracking-[-0.00875rem] mb-0 cursor-default">
                    {item.label}
                  </Label>
                </div>
              ))}
            </div>
            {errors.preferredDays && (
              <p className="mt-1 text-xs text-red-500">{errors.preferredDays.message}</p>
            )}
          </div>
          <div className="">
            <h6 className="text-[#4D5358] text-xs font-light leading-[1.05rem] mb-2">
              Preferred Appointment Time (Select all the time windows that work for you)
            </h6>
            <div className="flex gap-3 items-center">
              <div className="flex gap-1 items-center">
                <SmallCheckbox 
                  id="am"
                  className="size-3 border-[0.6px] border-[#343A3F] rounded-[0.0625rem] shadow-none"
                  checked={preferredTimes?.includes('AM') || false}
                  onCheckedChange={(checked) => handleTimeChange('AM', checked as boolean)}
                />
                <Label htmlFor="am" className="text-[#343A3F] text-sm font-normal leading-[1.05rem] tracking-[-0.00875rem] mb-0 cursor-default">
                  AM
                </Label>
              </div>
              <div className="flex gap-1 items-center">
                <SmallCheckbox 
                  id="pm"
                  className="size-3 border-[0.6px] border-[#343A3F] rounded-[0.0625rem] shadow-none"
                  checked={preferredTimes?.includes('PM') || false}
                  onCheckedChange={(checked) => handleTimeChange('PM', checked as boolean)}
                />
                <Label htmlFor="pm" className="text-[#343A3F] text-sm font-normal leading-[1.05rem] tracking-[-0.00875rem] mb-0 cursor-default">
                  PM
                </Label>
              </div>
            </div>
            {errors.preferredTimes && (
              <p className="mt-1 text-xs text-red-500">{errors.preferredTimes.message}</p>
            )}
          </div>
          <div className="">
            <Label
              htmlFor="servicesInterested"
              className="w-fit text-[#4D5358] text-xs font-light leading-[1.05rem] mb-1"
            >
              Services Interested
            </Label>
            <Input
              id="servicesInterested"
              type="text"
              className={`bg-white h-[2.4375rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 ${errors.servicesInterested ? 'border-red-500' : ''}`}
              placeholder="|"
              {...register('servicesInterested')}
            />
            {errors.servicesInterested && (
              <p className="mt-1 text-xs text-red-500">{errors.servicesInterested.message}</p>
            )}
          </div>
          <div className="">
            <Label
              htmlFor="message"
              className="w-fit text-[#4D5358] text-xs font-light leading-[1.05rem] mb-1"
            >
              Message
            </Label>
            <Textarea
              id="message"
              className="bg-white min-h-[6.125rem] max-h-[6.125rem] p-3 border-[#DDE1E6] rounded-lg text-[#4D5358] placeholder:text-[#878D96] text-xs font-normal leading-normal focus:outline-none focus:ring-1 focus:ring-blue-200 resize-none"
              placeholder="|"
              {...register('message')}
            />
          </div>
          {selectedPatientType === 'new' && (
          <div className="">
            <h6 className="text-[#4D5358] text-xs font-light leading-[1.05rem] mb-2">
              How did patient hear about us?
            </h6>
            <div className="flex flex-wrap gap-y-2 gap-x-[2.875rem] items-center">
              {howDidYouHearAboutUs.map((item) => (
                <div key={item.id} className="flex gap-2 items-center">
                  <SmallCheckbox 
                    id={item.id}
                    className="size-[0.6875rem] border-[0.6px] border-[#343A3F] rounded-[0.125rem] shadow-none"
                    checked={source?.includes(item.id) || false}
                    onCheckedChange={(checked) => handleSourceChange(item.id, checked as boolean)}
                  />
                  <Label htmlFor={item.id} className="text-[#343A3F] text-sm font-normal leading-normal mb-0 cursor-default">
                    {item.label}
                  </Label>
                </div>
              ))}
            </div>
            {errors.source && (
              <p className="mt-1 text-xs text-red-500">{errors.source.message}</p>
            )}
          </div>
          )}
          {isError && (
            <div className="p-3 text-sm text-red-500 bg-red-50 rounded-lg">
              Failed to create appointment request. Please try again.
            </div>
          )}
          <div className="flex justify-end pt-1">
            <Button
             onClick={handleSubmit(handleFormSubmit)}
              variant="primary"
              className="w-fit"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
